import React from "react";
import Image from "next/image";
import { Eye, EyeOff, TriangleAlert, CheckCircle } from "lucide-react";
import PhoneInput, { type Value as PhoneValue } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import TermsAndCondition from "../termsAndCondition";
import {
  useAuthForm,
  type AuthFormData,
  type AuthFormMessage,
} from "../../hooks/useAuthForm";

export type { AuthFormData };

interface AuthFormProps {
  onSubmit: (data: AuthFormData) => void;
  isLoading: boolean;
  message: AuthFormMessage;
  onRedirect: () => void;
}

interface FieldProps {
  id: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled?: boolean;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  minLength?: number;
  required?: boolean;
  rightSlot?: React.ReactNode;
}

function InputField({
  id, name, type = "text", value, onChange, placeholder,
  disabled, maxLength, inputMode, minLength, required, rightSlot,
}: FieldProps) {
  return (
    <div className="auth-field">
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        inputMode={inputMode}
        minLength={minLength}
        required={required}
        className="auth-field-input"
      />
      {rightSlot}
    </div>
  );
}

function PhoneField({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value?: PhoneValue;
  onChange: (value?: PhoneValue) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <div className="auth-field auth-phone-input">
      <PhoneInput
        id="mobile"
        international
        defaultCountry="IN"
        countryCallingCodeEditable={false}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        numberInputProps={{
          name: "mobile",
          required: true,
        }}
      />
    </div>
  );
}

const AuthForm: React.FC<AuthFormProps> = ({ onSubmit, isLoading, message, onRedirect }) => {
  const {
    formData,
    mobileValue,
    showPassword,
    showTermsModal,
    termsAccepted,
    passwordError,
    passwordSuccess,
    alertVisible,
    handleInputChange,
    handleMobileChange,
    handleSubmit,
    togglePasswordVisibility,
    toggleTermsAccepted,
    acceptTerms,
    openTermsModal,
    closeTermsModal,
  } = useAuthForm({ onSubmit, message });

  return (
    <div className="auth-form-card">
      {message && alertVisible && (
        <div
          role="alert"
          className={`auth-form-alert auth-form-alert--${message.type}`}
        >
          {message.type === "success"
            ? <CheckCircle size={16} className="auth-form-alert-icon" />
            : <TriangleAlert size={16} className="auth-form-alert-icon" />
          }
          <span>{message.text}</span>
        </div>
      )}

      <div className="auth-form-header">
        <Image
          src="/onecaplogo.svg"
          alt="OneCap"
          width={140}
          height={36}
          className="auth-form-logo"
          priority
        />
        <h2 className="auth-form-title">
          Your first <span className="auth-form-title-count">10</span> reconciliations
          are <span className="auth-form-title-accent">free</span>
        </h2>
        <div className="auth-form-note">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          No credit card needed
        </div>
      </div>

      <form onSubmit={handleSubmit} className="auth-form-fields">
        <div className="auth-form-field">
          <label htmlFor="email" className="auth-form-label">Email address</label>
          <InputField
            id="email" name="email" type="email" required
            value={formData.email} onChange={handleInputChange}
            placeholder="you@example.com" disabled={isLoading}
          />
        </div>

        <div className="auth-form-field">
          <label htmlFor="mobile" className="auth-form-label">Mobile number</label>
          <PhoneField
            value={mobileValue}
            onChange={handleMobileChange}
            disabled={isLoading}
            placeholder="98765 43210"
          />
        </div>

        <div className="auth-form-field">
          <label htmlFor="password" className="auth-form-label">Password</label>
          <InputField
            id="password" name="password"
            type={showPassword ? "text" : "password"} required
            value={formData.password} onChange={handleInputChange}
            placeholder="At least 8 characters" disabled={isLoading} minLength={8}
            rightSlot={
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="auth-field-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={17} strokeWidth={1.75} /> : <Eye size={17} strokeWidth={1.75} />}
              </button>
            }
          />
          {formData.password.length > 0 && formData.password.length < 8 && (
            <p className="auth-form-hint">
              Password must be at least 8 characters long.
            </p>
          )}
          {passwordError && (
            <div className="auth-form-status auth-form-status--error">
              <TriangleAlert size={14} className="auth-form-status-icon" />
              <span>{passwordError}</span>
            </div>
          )}
          {passwordSuccess && (
            <div className="auth-form-status auth-form-status--success">
              <CheckCircle size={14} className="auth-form-status-icon" />
              <span>Password meets all requirements.</span>
            </div>
          )}
        </div>

        <div className="auth-form-terms">
          <input
            type="checkbox"
            id="terms"
            checked={termsAccepted}
            onChange={toggleTermsAccepted}
            className="auth-form-terms-checkbox"
          />
          <label htmlFor="terms" className="auth-form-terms-label">
            I agree to the{" "}
            <button
              type="button"
              onClick={openTermsModal}
              className="auth-form-text-btn auth-form-text-btn--underline"
            >
              Terms and Conditions
            </button>
          </label>
        </div>

        <button type="submit" disabled={isLoading} className="auth-form-submit">
          {isLoading ? (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="auth-form-submit-spinner"
                aria-hidden
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              Creating account…
            </>
          ) : (
            <>
              Create account
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M5 12h13M13 6l6 6-6 6" />
              </svg>
            </>
          )}
        </button>

        <p className="auth-form-footer">
          Already have an account?{" "}
          <button type="button" onClick={onRedirect} className="auth-form-text-btn">
            Log in
          </button>
        </p>
      </form>

      {showTermsModal && (
        <TermsAndCondition onClose={closeTermsModal} onAccept={acceptTerms} />
      )}
    </div>
  );
};

export default AuthForm;
